// scenarioSourceSlice — manages multi-source scenario navigation.
//
// Three independent sources: "example" (TicketWave from GitHub), "user" (uploaded files),
// and "websocket" (live connection to `moment serve`).
// Each source has its own manifest, cached scenarios/topologies, and active scenario ID.
// Only one source is active at a time; this slice does not reset walker/simulation
// state on source switches — consumers (sagas, components) handle that.

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  SimulationScenario,
  SimulationTopology,
  FlowManifest,
  FlowEntry,
  ManifestEntry,
} from '../../types/index.ts';
import type { WsConnectionStatus, WsErrorPhase, WsManifest } from '../../types/websocket.ts';
import { WS_DEFAULT_HOST, WS_DEFAULT_PORT, WS_DEFAULT_PROTOCOL } from '../../types/websocket.ts';
import type { RootState } from '../index.ts';

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

export type ScenarioSourceId = 'example' | 'user' | 'websocket';

export interface SourceState {
  manifest: FlowManifest | null;
  loadedScenarios: Record<string, SimulationScenario>;
  loadedTopologies: Record<string, SimulationTopology>;
  activeScenarioId: string | null;
  activeFlowId: string | null;
  loading: boolean;
  error: string | null;
  warnings: string[];
}

export interface WebSocketSourceState extends SourceState {
  connectionStatus: WsConnectionStatus;
  host: string;
  port: number;
  secure: boolean;
  sessionId: string | null;
  specFile: string | null;
  errorMessage: string | null;
  /** Raw WS manifest — kept so scenario-update can rebuild the derived FlowManifest. */
  rawWsManifest: WsManifest | null;
}

export interface ScenarioSourceState {
  activeSource: ScenarioSourceId;
  example: SourceState;
  user: SourceState;
  websocket: WebSocketSourceState;
}

function emptySource(): SourceState {
  return {
    manifest: null,
    loadedScenarios: {},
    loadedTopologies: {},
    activeScenarioId: null,
    activeFlowId: null,
    loading: false,
    error: null,
    warnings: [],
  };
}

function emptyWebSocketSource(): WebSocketSourceState {
  return {
    ...emptySource(),
    connectionStatus: 'disconnected',
    host: WS_DEFAULT_HOST,
    port: WS_DEFAULT_PORT,
    secure: WS_DEFAULT_PROTOCOL === 'wss',
    sessionId: null,
    specFile: null,
    errorMessage: null,
    rawWsManifest: null,
  };
}

const initialState: ScenarioSourceState = {
  activeSource: 'example',
  example: emptySource(),
  user: emptySource(),
  websocket: emptyWebSocketSource(),
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const scenarioSourceSlice = createSlice({
  name: 'scenarioSource',
  initialState,
  reducers: {
    setActiveSource(state, action: PayloadAction<ScenarioSourceId>) {
      state.activeSource = action.payload;
    },

    manifestLoaded(
      state,
      action: PayloadAction<{ source: ScenarioSourceId; manifest: FlowManifest }>,
    ) {
      const { source, manifest } = action.payload;
      state[source].manifest = manifest;
      state[source].error = null;
    },

    scenarioLoaded(
      state,
      action: PayloadAction<{
        source: ScenarioSourceId;
        scenarioId: string;
        data: SimulationScenario;
      }>,
    ) {
      const { source, scenarioId, data } = action.payload;
      state[source].loadedScenarios[scenarioId] = data;
    },

    topologyLoaded(
      state,
      action: PayloadAction<{
        source: ScenarioSourceId;
        flowId: string;
        data: SimulationTopology;
      }>,
    ) {
      const { source, flowId, data } = action.payload;
      state[source].loadedTopologies[flowId] = data;
    },

    setActiveScenario(
      state,
      action: PayloadAction<{ source: ScenarioSourceId; scenarioId: string; flowId: string }>,
    ) {
      const { source, scenarioId, flowId } = action.payload;
      state[source].activeScenarioId = scenarioId;
      state[source].activeFlowId = flowId;
    },

    setLoading(state, action: PayloadAction<{ source: ScenarioSourceId; loading: boolean }>) {
      state[action.payload.source].loading = action.payload.loading;
    },

    setError(state, action: PayloadAction<{ source: ScenarioSourceId; error: string | null }>) {
      const { source, error } = action.payload;
      state[source].error = error;
      state[source].loading = false;
    },

    setWarnings(state, action: PayloadAction<{ source: ScenarioSourceId; warnings: string[] }>) {
      state[action.payload.source].warnings = action.payload.warnings;
    },

    clearSource(state, action: PayloadAction<ScenarioSourceId>) {
      const source = action.payload;
      if (source === 'websocket') {
        // Preserve connection config when clearing data, but reset any
        // websocket-specific session/error state to avoid stale UI.
        const ws = state.websocket;
        const { host, port, secure, connectionStatus } = ws;
        Object.assign(ws, emptySource(), {
          host,
          port,
          secure,
          connectionStatus,
          sessionId: null,
          specFile: null,
          errorMessage: null,
          rawWsManifest: null,
        });
      } else {
        state[source] = emptySource();
      }
    },

    // ── WebSocket-specific reducers ─────────────────────────────────────

    wsConnectionStatusChanged(state, action: PayloadAction<WsConnectionStatus>) {
      state.websocket.connectionStatus = action.payload;
      if (action.payload === 'connected') {
        state.websocket.errorMessage = null;
      }
    },

    wsSessionEstablished(state, action: PayloadAction<{ sessionId: string; specFile: string }>) {
      state.websocket.sessionId = action.payload.sessionId;
      state.websocket.specFile = action.payload.specFile;
    },

    wsErrorReceived(state, action: PayloadAction<{ phase: WsErrorPhase; message: string }>) {
      state.websocket.errorMessage = `[${action.payload.phase}] ${action.payload.message}`;
    },

    wsErrorDismissed(state) {
      state.websocket.errorMessage = null;
    },

    wsRawManifestStored(state, action: PayloadAction<WsManifest>) {
      state.websocket.rawWsManifest = action.payload;
    },

    wsConfigUpdated(state, action: PayloadAction<{ host: string; port: number; secure: boolean }>) {
      state.websocket.host = action.payload.host;
      state.websocket.port = action.payload.port;
      state.websocket.secure = action.payload.secure;
    },

    wsDisconnected(state) {
      state.websocket.connectionStatus = 'disconnected';
      state.websocket.sessionId = null;
      state.websocket.specFile = null;
    },
  },
});

export const {
  setActiveSource,
  manifestLoaded,
  scenarioLoaded,
  topologyLoaded,
  setActiveScenario,
  setLoading,
  setError,
  setWarnings,
  clearSource,
  wsConnectionStatusChanged,
  wsSessionEstablished,
  wsErrorReceived,
  wsErrorDismissed,
  wsRawManifestStored,
  wsConfigUpdated,
  wsDisconnected,
} = scenarioSourceSlice.actions;

export default scenarioSourceSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectActiveSource = (state: RootState) => state.scenarioSource.activeSource;

export const selectActiveSourceState = (state: RootState) =>
  state.scenarioSource[state.scenarioSource.activeSource];

export const selectManifest = (state: RootState) => selectActiveSourceState(state).manifest;

export const selectActiveScenarioId = (state: RootState) =>
  selectActiveSourceState(state).activeScenarioId;

export const selectActiveFlowId = (state: RootState) => selectActiveSourceState(state).activeFlowId;

export const selectSourceLoading = (state: RootState) => selectActiveSourceState(state).loading;

export const selectSourceError = (state: RootState) => selectActiveSourceState(state).error;

export const selectSourceWarnings = (state: RootState) => selectActiveSourceState(state).warnings;

export const selectCachedScenario = (state: RootState, scenarioId: string) =>
  selectActiveSourceState(state).loadedScenarios[scenarioId] ?? null;

export const selectCachedTopology = (state: RootState, flowId: string) =>
  selectActiveSourceState(state).loadedTopologies[flowId] ?? null;

export const selectSourceManifest = (state: RootState, source: ScenarioSourceId) =>
  state.scenarioSource[source].manifest;

/** Find the FlowEntry for a given flowId in a manifest. */
export function findFlow(manifest: FlowManifest | null, flowId: string): FlowEntry | undefined {
  return manifest?.flows.find((f) => f.flowId === flowId);
}

/** Find the first happy path entry across all flows. Returns [flow, entry] or undefined. */
export function findFirstHappyPath(
  manifest: FlowManifest | null,
): [FlowEntry, ManifestEntry] | undefined {
  if (!manifest) return undefined;
  for (const flow of manifest.flows) {
    const happy = flow.scenarios.find((s) => s.isHappyPath);
    if (happy) return [flow, happy];
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// WebSocket selectors
// ---------------------------------------------------------------------------

export const selectWebSocketState = (state: RootState) => state.scenarioSource.websocket;

export const selectWsConnectionStatus = (state: RootState) =>
  state.scenarioSource.websocket.connectionStatus;

export const selectWsErrorMessage = (state: RootState) =>
  state.scenarioSource.websocket.errorMessage;
